import { supabase } from '../lib/supabaseClient';

// Use native crypto.randomUUID() for UUID generation
const generateUUID = (): string => {
  return crypto.randomUUID();
};

// ============================================
// Types
// ============================================

interface SessionData {
  sessionId: string;
  ipAddress: string | null;
  userAgent: string;
  country: string | null;
  city: string | null;
  deviceType: 'mobile' | 'tablet' | 'desktop';
}

interface SpaModel {
  id: string;
  name: string;
  price?: number;
  productUrl?: string;
  capacity?: number;
  dimensions?: {
    length: number;
    width: number;
  };
}

// ============================================
// Session Management
// ============================================

const SESSION_KEY = 'mspa_session_id';
const SESSION_DATA_KEY = 'mspa_session_data';
const SESSION_INIT_FLAG = 'mspa_session_initializing';
const SESSION_GEO_FLAG = 'mspa_session_geo_done';

// Shared promise that resolves once the analytics_sessions row for this tab exists.
// Every tracker awaits this before inserting, so an event can never lose the
// foreign-key race against session creation, regardless of which fires first on load.
let sessionReadyPromise: Promise<boolean> | null = null;

/**
 * Get or create a session ID for this user
 */
function getSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_KEY);

  if (!sessionId) {
    sessionId = generateUUID();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }

  return sessionId;
}

/**
 * Detect device type from user agent
 */
function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const ua = navigator.userAgent.toLowerCase();

  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Get IP address and location data
 * Uses ipapi.co free tier (1000 requests/day)
 */
async function getIpAndLocation(): Promise<{ ip: string | null; country: string | null; city: string | null }> {
  try {
    const response = await fetch('https://ipapi.co/json/', {
      signal: AbortSignal.timeout(3000), // 3 second timeout
    });

    if (!response.ok) {
      throw new Error('IP lookup failed');
    }

    const data = await response.json();

    return {
      ip: data.ip || null,
      country: data.country_name || null,
      city: data.city || null,
    };
  } catch (error) {
    console.warn('Failed to fetch IP/location:', error);
    return { ip: null, country: null, city: null };
  }
}

/**
 * Ensure the analytics_sessions row for this tab exists. Idempotent and race-safe:
 * the first caller creates the row, every other caller awaits the same promise.
 * The (minimal) row is inserted up front — before the slow IP/geo lookup — so that
 * every subsequent event already has its foreign-key parent and records reliably.
 */
function ensureSession(): Promise<boolean> {
  if (!supabase) return Promise.resolve(false);
  if (!sessionReadyPromise) {
    sessionReadyPromise = createSessionRow();
  }
  return sessionReadyPromise;
}

async function createSessionRow(): Promise<boolean> {
  if (!supabase) return false;

  const sessionId = getSessionId();

  // Already created earlier in this tab — just refresh last_seen.
  if (sessionStorage.getItem(SESSION_DATA_KEY)) {
    await supabase
      .from('analytics_sessions')
      .update({ last_seen: new Date().toISOString() })
      .eq('session_id', sessionId);
    return true;
  }

  // Insert the row immediately with minimal data; geo is filled in afterwards.
  const deviceType = getDeviceType();
  const { error } = await supabase
    .from('analytics_sessions')
    .insert({
      session_id: sessionId,
      ip_address: null,
      user_agent: navigator.userAgent,
      country: null,
      city: null,
      device_type: deviceType,
    });

  // 23505 = unique violation: a concurrent tab/call already inserted the row. That's fine.
  if (error && (error as { code?: string }).code !== '23505') {
    console.error('Failed to create session:', error);
    return false;
  }

  const sessionData: SessionData = {
    sessionId,
    ipAddress: null,
    userAgent: navigator.userAgent,
    country: null,
    city: null,
    deviceType,
  };
  sessionStorage.setItem(SESSION_DATA_KEY, JSON.stringify(sessionData));
  sessionStorage.setItem(SESSION_INIT_FLAG, 'true');
  return true;
}

/**
 * Initialize session tracking. Creates the session row (race-safe) and then
 * enriches it with IP/geo in the background.
 */
export async function initializeSession(): Promise<void> {
  if (!supabase) {
    console.warn('Supabase not configured, skipping analytics');
    return;
  }
  const ok = await ensureSession();
  if (ok) void enrichSessionGeo();
}

/**
 * Best-effort IP/geo enrichment via an UPDATE. The session row already exists by now,
 * so a slow or failed lookup never blocks event tracking. Runs at most once per tab.
 */
async function enrichSessionGeo(): Promise<void> {
  if (!supabase) return;
  if (sessionStorage.getItem(SESSION_GEO_FLAG) === 'true') return;
  sessionStorage.setItem(SESSION_GEO_FLAG, 'true');

  const sessionId = getSessionId();
  const { ip, country, city } = await getIpAndLocation();
  if (!ip && !country && !city) return;

  await supabase
    .from('analytics_sessions')
    .update({ ip_address: ip, country, city })
    .eq('session_id', sessionId);

  // Keep the cached copy in sync.
  const cached = sessionStorage.getItem(SESSION_DATA_KEY);
  if (cached) {
    try {
      const data = JSON.parse(cached);
      data.ipAddress = ip;
      data.country = country;
      data.city = city;
      sessionStorage.setItem(SESSION_DATA_KEY, JSON.stringify(data));
    } catch {
      // ignore cache update failure
    }
  }
}

// ============================================
// Event Tracking
// ============================================

/**
 * Track a generic event
 */
export async function trackEvent(eventType: string, eventCategory: string, eventData?: Record<string, unknown>): Promise<void> {
  if (!supabase) return;
  await ensureSession();

  const sessionId = getSessionId();

  const { error } = await supabase
    .from('analytics_events')
    .insert({
      session_id: sessionId,
      event_type: eventType,
      event_category: eventCategory,
      event_data: eventData || {},
    });

  if (error) {
    console.error('Failed to track event:', error);
  }
}

// ============================================
// Image Upload Tracking
// ============================================

export async function trackImageUpload(method: 'camera' | 'file' | 'drag-drop' | 'example' | 'ai-generated', success: boolean, imageType?: string, errorMessage?: string): Promise<void> {
  await trackEvent('image_upload', 'upload', {
    method,
    success,
    image_type: imageType,
    error_message: errorMessage,
  });
}

// ============================================
// Spa Model Tracking
// ============================================

export async function trackSpaModelViewed(spa: SpaModel): Promise<void> {
  if (!supabase) return;
  await ensureSession();

  const sessionId = getSessionId();

  await supabase
    .from('spa_models_viewed')
    .insert({
      session_id: sessionId,
      spa_model_name: spa.name,
      spa_model_id: spa.id,
    });

  await trackEvent('spa_model_viewed', 'selection', {
    model_id: spa.id,
    model_name: spa.name,
    price: spa.price,
  });
}

export async function trackSpaModelSelected(spa: SpaModel): Promise<void> {
  await trackEvent('spa_model_selected', 'selection', {
    model_id: spa.id,
    model_name: spa.name,
    price: spa.price,
  });
}

// ============================================
// Visualization Tracking
// ============================================

export async function trackVisualizationStarted(spa: SpaModel, uploadMethod: string, imageType: string): Promise<string> {
  if (!supabase) return '';

  const startTime = Date.now();

  // Store start time in sessionStorage for calculating processing time
  const vizId = generateUUID();
  sessionStorage.setItem(`viz_start_${vizId}`, startTime.toString());
  sessionStorage.setItem(`viz_data_${vizId}`, JSON.stringify({ spa, uploadMethod, imageType }));

  await trackEvent('visualization_started', 'visualization', {
    model_id: spa.id,
    model_name: spa.name,
    upload_method: uploadMethod,
    image_type: imageType,
  });

  return vizId;
}

export async function trackVisualizationCompleted(vizId: string, success: boolean, errorMessage?: string): Promise<void> {
  if (!supabase) return;
  await ensureSession();

  const sessionId = getSessionId();
  const startTime = sessionStorage.getItem(`viz_start_${vizId}`);
  const vizDataStr = sessionStorage.getItem(`viz_data_${vizId}`);

  if (!startTime || !vizDataStr) {
    console.warn('Visualization tracking data not found');
    return;
  }

  const processingTime = Date.now() - parseInt(startTime, 10);
  const vizData = JSON.parse(vizDataStr);

  // Store in visualizations_created table
  await supabase
    .from('visualizations_created')
    .insert({
      session_id: sessionId,
      spa_model_id: vizData.spa.id,
      spa_model_name: vizData.spa.name,
      upload_method: vizData.uploadMethod,
      image_type: vizData.imageType,
      success,
      error_message: errorMessage || null,
      processing_time_ms: processingTime,
    });

  await trackEvent(success ? 'visualization_completed' : 'visualization_failed', 'visualization', {
    model_id: vizData.spa.id,
    model_name: vizData.spa.name,
    processing_time_ms: processingTime,
    error_message: errorMessage,
  });

  // Clean up
  sessionStorage.removeItem(`viz_start_${vizId}`);
  sessionStorage.removeItem(`viz_data_${vizId}`);
}

export async function trackVisualizationAdjustment(adjustmentType: 'position' | 'lighting' | 'time_of_day', adjustmentData: Record<string, unknown>): Promise<void> {
  await trackEvent('visualization_adjustment', 'visualization', {
    adjustment_type: adjustmentType,
    ...adjustmentData,
  });
}

export async function trackRegeneration(spa: SpaModel): Promise<void> {
  await trackEvent('visualization_regeneration', 'visualization', {
    model_id: spa.id,
    model_name: spa.name,
  });
}

// ============================================
// Download Tracking
// ============================================

export async function trackDownload(spa: SpaModel | null, imageType: 'original' | 'visualization'): Promise<void> {
  if (!supabase) return;
  await ensureSession();

  const sessionId = getSessionId();
  const deviceType = getDeviceType();

  await supabase
    .from('downloads')
    .insert({
      session_id: sessionId,
      spa_model_id: spa?.id || null,
      spa_model_name: spa?.name || null,
      device_type: deviceType,
      image_type: imageType,
    });

  await trackEvent('download', 'download', {
    model_id: spa?.id,
    model_name: spa?.name,
    device_type: deviceType,
    image_type: imageType,
  });
}

// ============================================
// Product Click Tracking (Conversion)
// ============================================

export async function trackProductClick(spa: SpaModel, productUrl: string): Promise<void> {
  if (!supabase) return;
  await ensureSession();

  const sessionId = getSessionId();

  await supabase
    .from('product_clicks')
    .insert({
      session_id: sessionId,
      spa_model_id: spa.id,
      spa_model_name: spa.name,
      product_url: productUrl,
    });

  await trackEvent('product_click', 'conversion', {
    model_id: spa.id,
    model_name: spa.name,
    product_url: productUrl,
  });
}

// ============================================
// Navigation Tracking
// ============================================

export async function trackPageView(pageName: string): Promise<void> {
  await trackEvent('page_view', 'navigation', {
    page: pageName,
  });
}

export async function trackButtonClick(buttonName: string, location: string): Promise<void> {
  await trackEvent('button_click', 'navigation', {
    button: buttonName,
    location,
  });
}

// ============================================
// Error Tracking
// ============================================

export async function trackError(errorType: string, errorMessage: string, errorContext?: Record<string, unknown>): Promise<void> {
  await trackEvent('error', 'error', {
    error_type: errorType,
    error_message: errorMessage,
    ...errorContext,
  });
}

// ============================================
// Initialize on import
// ============================================

// Note: We'll call initializeSession() from App.tsx on mount
// This ensures it only runs once per session
